import React from 'react';

import { useDroppable } from '@dnd-kit/core';

import type { UnifiedFeeItem, UnifiedOtherService } from '../../types';

export interface GapDropZoneProps {
	id: string;
	categoryId: string;
	isDragging: boolean;
	disabled?: boolean;
	itemAbove?: UnifiedFeeItem | UnifiedOtherService; // Item above the gap (for display_order calculation)
	itemBelow?: UnifiedFeeItem | UnifiedOtherService; // Item below the gap (for display_order calculation)
}

/**
 * A droppable zone that appears in the gap between two items.
 * When an item is dropped here, it becomes a root/parent item.
 */
export const GapDropZone: React.FC<GapDropZoneProps> = ({
	id,
	categoryId,
	isDragging,
	disabled = false,
	itemAbove,
	itemBelow,
}) => {
	const droppable = useDroppable({
		id: `gap-${id}`,
		disabled: disabled || !isDragging,
		data: {
			type: 'gap',
			categoryId,
			// This indicates it's a gap drop zone for root items
			dropAsParent: true,
			// Include items above and below for display_order calculation
			itemAbove,
			itemBelow,
		},
	});

	const isOver = droppable.isOver;
	const setNodeRef = droppable.setNodeRef;

	// Only show when dragging
	if (!isDragging) {
		return null;
	}

	return (
		<div
			ref={setNodeRef}
			className={`absolute left-0 right-0 w-full transition-all duration-200 pointer-events-auto ${
				isOver
					? 'h-8 bg-blue-100 border-y-2 border-blue-500 border-dashed z-10'
					: 'h-4 bg-transparent hover:bg-blue-50/50 z-0'
			}`}
			style={{
				minHeight: isOver ? '32px' : '16px', // Increased from 8px to 16px for better collision detection
				// Position absolutely at the bottom edge of the row container, extending into the gap
				// This ensures it doesn't affect the row height and keeps columns aligned
				bottom: '-8px', // Center it in the gap (half of h-4 = 16px / 2 = 8px)
			}}
		>
			{isOver && (
				<div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex items-center justify-center pointer-events-none z-20">
					<div className="bg-blue-600 text-white px-4 py-1.5 rounded-lg shadow-lg text-sm font-semibold animate-pulse flex items-center gap-2">
						<span>↓</span>
						<span>Drop as parent</span>
						<span>↓</span>
					</div>
				</div>
			)}
		</div>
	);
};
